import type { Item } from "./types";
import { getCornerZone, getSeatSlots, isWorkstationShape, rotateAround } from "./workstation-layout";

const uid = () => Math.random().toString(36).slice(2, 10);

/**
 * Break a parametric workstation item into independently-editable parts:
 * one desk item, N chair items, and (for L-shaped) one hatched corner zone.
 * All parts share a fresh groupId. The desk carries the `origin` metadata so
 * the group can be merged back into the original parametric shape later.
 */
export function explodeWorkstation(item: Item): Item[] {
  if (!isWorkstationShape(item.shape)) return [item];
  const groupId = uid();
  const slots = getSeatSlots(item.shape, item.w, item.h, item.seats);
  const origin = {
    shape: item.shape,
    w: item.w,
    h: item.h,
    seats: item.seats,
    color: item.color,
    label: item.label,
  };

  const cx = item.x + item.w / 2;
  const cy = item.y + item.h / 2;

  const desk: Item = {
    id: uid(),
    type: "desk-rect",
    kind: "architectural", // doesn't block furniture collision
    shape: "desk-rect",
    label: `${item.label} · Desk`,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    rotation: item.rotation,
    color: item.color,
    groupId,
    origin,
  };

  const chairs: Item[] = slots.map((s, idx) => {
    const localX = item.x + s.cx;
    const localY = item.y + s.cy;
    const p = rotateAround(localX, localY, cx, cy, item.rotation);
    return {
      id: uid(),
      type: "chair",
      kind: "furniture",
      shape: "chair",
      label: `Chair ${idx + 1}`,
      x: p.x - s.w / 2,
      y: p.y - s.h / 2,
      w: s.w,
      h: s.h,
      rotation: (item.rotation + s.rot) % 360,
      groupId,
      slotIndex: idx,
    };
  });

  const parts: Item[] = [desk, ...chairs];

  const zone = getCornerZone(item.shape, item.w, item.h);
  if (zone) {
    const zCenterLocal = { x: item.x + zone.w / 2, y: item.y + zone.h / 2 };
    const zCenter = rotateAround(zCenterLocal.x, zCenterLocal.y, cx, cy, item.rotation);
    parts.push({
      id: uid(),
      type: "zone-hatched",
      kind: "architectural",
      shape: "zone-hatched",
      label: "Unoccupiable corner",
      x: zCenter.x - zone.w / 2,
      y: zCenter.y - zone.h / 2,
      w: zone.w,
      h: zone.h,
      rotation: item.rotation,
      groupId,
    });
  }

  return parts;
}